import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  withStyles,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Tooltip,
  CircularProgress,
  Typography,
  Grow,
  Divider,
} from '@material-ui/core';

// actions
import { resetNewEvents } from '@pingum/app-state-management/event/actions';

// widgets
import EventHeaderDetails from '../../../../widgets/EventHeaderDetails';
import Property from '../../../../widgets/Property';
import User from '../../../../widgets/User';
import StatusBadge from '../../../../widgets/StatusBadge';
import DueDate from '../../../../widgets/DueDate';

// associated components
import EventActions from '../../EventActions';

// utils
import {
  getColorForItem,
  getIconForItem,
} from '../../../../../utils/iconHelper';
import { formatDateString } from '../../../../../utils/dateHelper';

import styles from './styles';

export const buildGridAndItems = (
  events,
  isNewEvent,
  classes,
  statuses,
  eventData,
  handleRowClick,
  handleChangeStatusClick,
  handleResetNewEvents,
  restricted,
  selectedColumns,
) => (
  <Grid container spacing={2}>
    {
      events.map((event) => {
        const eventActions = (
          <EventActions
            event={event}
            openEventDetailsPage={() => handleRowClick(event.id, true)}
            openHistoryDialog={() => handleChangeStatusClick(event)}
          />
        );

        return (
          <Grow
            in
            key={
              event.isUpdated
                ? `${event.id}-update`
                : event.id
            } // IMPORTANT needed so the tranistion is triggered
            timeout={500}
          >
            <Grid
              item
              sm={12}
              md={6}
              lg={4}
              key={event.id}
              onFocus={() => { isNewEvent && handleResetNewEvents(); }}
              onMouseOver={() => { isNewEvent && handleResetNewEvents(); }}
            >
              <Card
                onClick={() => handleRowClick(event.id)}
                classes={{ root: classes.card }}
              >
                <CardHeader
                  classes={{
                    root: classes.cardHeader,
                    avatar: classes.avatar,
                  }}
                  avatar={(
                    <StatusBadge
                      icon={getIconForItem(statuses, event.currentEventStatus
                        ? event.currentEventStatus.status.id
                        : '')}
                      iconColor={
                        getColorForItem(statuses, event.currentEventStatus
                          ? event.currentEventStatus.status.id
                          : '')
                      }
                    />
                  )}
                  title={
                    <EventHeaderDetails event={event} showStatus statuses={statuses} />
                  }
                  action={
                    !restricted
                      && eventActions
                  }
                />
                <CardContent
                  classes={{ root: classes.cardContent }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs>
                      <Property label="Assignment">
                        {(event.currentEventStatus && event.currentEventStatus.assignment)
                          ? (event.currentEventStatus.assignment.split(/(?=@\[)/)
                            .map((user) => (
                              <User
                                key={`event-${event.id}-user-${user.match(/@\[.*\]\((.*)\)/)[1]}`}
                                chip
                                user={{ auth0UserId: user.match(/@\[.*\]\((.*)\)/)[1] }}
                              />
                            )))
                          : (
                            '-'
                          )}
                      </Property>
                    </Grid>
                    {Object.keys((selectedColumns || [])[0] || {}).map((highlight) => ((event.eventData || {})[highlight]
                      && (
                        <Grid item xs={12}>
                          <Property label={highlight}>
                            {(event.eventData || {})[highlight]
                              ? (event.eventData || {})[highlight].value
                              : '-'}
                          </Property>
                        </Grid>
                      )
                    ))}
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs>
                      <Property label="Due Date">
                        <DueDate dueDate={event.dueDate} />
                      </Property>
                    </Grid>
                    <Grid item xs>
                      <Property
                        label="Created"
                        disableGutters
                      >
                        <Typography variant="body2">
                          {formatDateString(event.startDate)}
                        </Typography>
                      </Property>
                    </Grid>
                    <Grid item xs>
                      <Property
                        label="Last Updated"
                        disableGutters
                      >
                        <Typography variant="body2">
                          {formatDateString(event.updatedDate)}
                        </Typography>
                      </Property>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grow>
        );
      })
    }
  </Grid>
);

export class GridViewComponent extends React.Component {
  handleRowClick = (id, openInEditMode) => {
    this.props.handleSelectEvent(id, openInEditMode || false);
  }

  handleChangeStatusClick = (event) => {
    this.props.handleChangeStatusClick(event);
  }

  render() {
    const {
      classes,
      isAddingEvent,
      isUpdatingEvent,
      isChangingStatus,
      events,
      newEvents,
      statuses,
      eventData,
      restricted,
      currentFilterDashboard: { selectedColumns } = {},
    } = this.props;

    return (
      <div className={classes.viewContainer}>
        {
          buildGridAndItems(
            newEvents,
            true,
            classes,
            statuses,
            eventData,
            this.handleRowClick,
            this.handleChangeStatusClick,
            this.props.resetNewEvents,
            restricted,
            selectedColumns,
          )
        }
        {
          newEvents.length > 0
          && (
            <div>
              <span className={classes.newText}>new</span>
              <Divider className={classes.newDivider} />
            </div>
          )
        }
        {buildGridAndItems(
          events,
          false,
          classes,
          statuses,
          eventData,
          this.handleRowClick,
          this.handleChangeStatusClick,
          this.props.resetNewEvents,
          restricted,
          selectedColumns,
        )}
        {
          /* Putting this here because we need some prop update in this component
          on event add or update so this component rerenders with the new data. Without
          this the list won't add new events or update existing ones. */
          (isAddingEvent || isUpdatingEvent || isChangingStatus)
            && (
              <Tooltip title="Completing Action">
                <CircularProgress className={classes.isUpdatingProgress} />
              </Tooltip>
            )
        }
      </div>
    );
  }
}

GridViewComponent.propTypes = {
  classes: PropTypes.object,
  events: PropTypes.array,
  eventData: PropTypes.array,
  handleSelectEvent: PropTypes.func,
  newEvents: PropTypes.array,
  statuses: PropTypes.array,
  isAddingEvent: PropTypes.bool,
  isUpdatingEvent: PropTypes.bool,
  isChangingStatus: PropTypes.bool,
  resetNewEvents: PropTypes.func,
  handleChangeStatusClick: PropTypes.func,
  currentFilterDashboard: PropTypes.object,
  restricted: PropTypes.bool,
};

GridViewComponent.defaultProps = {
  classes: {},
  events: [],
  eventData: [],
  handleSelectEvent: () => {},
  newEvents: [],
  statuses: [],
  isAddingEvent: false,
  isUpdatingEvent: false,
  isChangingStatus: false,
  resetNewEvents: () => {},
  handleChangeStatusClick: () => {},
  restricted: false,
  currentFilterDashboard: { dashboardData: { statusIds: [] } },
};

export const mapStateToProps = (state) => ({
  events: state.event.eventsPageable.content,
  eventData: state.eventData.eventData,
  statuses: state.status.statuses,
  newEvents: state.event.newEvents,
  isAddingEvent: state.event.isAddingEvent,
  isUpdatingEvent: state.event.isUpdatingEvent,
  isChangingStatus: state.event.isChangingStatus,
  currentFilterDashboard: state.dashboard.currentFilterDashboard,
});

export const mapDispatchToProps = (dispatch) => ({ resetNewEvents: () => dispatch(resetNewEvents()) });

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles, { withTheme: true })(GridViewComponent)));
